import { createFileRoute, useParams } from '@tanstack/react-router';

import { HStack, VStack } from '@/components/Layout';
import AddStepperCompleteDialog from '@/components/Stepper/AddStepperCompleteDialog';
import Processing from '@/features/import-csv/-components/Processing';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { LIST_EMPTY_DATA } from '@/lib/listPage';

import AddStepperNavigation from '../../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../../-components/AddStepperStepTitleCard';
import BasicForm from '../../-components/BasicForm';
import ReviewTheImpactedRoleDataTableCard from '../../-components/ReviewTheImpactedRoleDataTableCard';
import SetPermission from '../../-components/SetPermission';
import { PRODUCT_PERMISSION_CREATE_HASH } from '../../-constants';
import useBasicForm from '../../-hooks/useBasicForm';
import useHash from '../../-hooks/useHash';
import useProductPermissionSetPermission from '../../-hooks/useProductPermissionSetPermission';
import useUpdateProductPermission from '../../-mutations/useUpdateProductPermission';
import useGetProductLevelAccessCategories from '../../-queries/useGetProductLevelAccessCategories';
import useGetProductPermissionDetail from '../../../../_detail/product-permissions/$id/-query/useGetProductPermissionDetail';
import { TabKey } from '../../../../_list/product-permissions/-constants';
import useGetProductPermissions from '../../../../_list/product-permissions/-queries/useGetProductPermissions';

export const Route = createFileRoute(
  '/_staff/settings/permission-and-roles/_form/product-permissions/$id/edit/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id: productLevelAccessPermissionId } = useParams({
    from: '/_staff/settings/permission-and-roles/_form/product-permissions/$id/edit/',
  });

  const BASE_LINK = `/settings/permission-and-roles/product-permissions/${productLevelAccessPermissionId}/edit`;

  const { data, isPending } = useGetProductPermissionDetail({
    productLevelAccessPermissionId,
  });

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createProductPermission',
  });

  const { hash } = useHash();

  const { data: productLevelAccessCategoriesRes } =
    useGetProductLevelAccessCategories();

  const { data: productPermissionsRes } = useGetProductPermissions({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 100,
    },
  });

  const { basicFormState, handleChange, isDuplicateName } = useBasicForm({
    data,
    existingProductPermissions: productPermissionsRes ?? LIST_EMPTY_DATA,
  });
  const {
    setPermissionState,
    checkCategory,
    checkScope,
    handleCategoryChange,
    handleScopeChange,
  } = useProductPermissionSetPermission({
    data,
    categories: productLevelAccessCategoriesRes,
  });

  const {
    mutate,
    isPending: mutatePending,
    isComplete,
  } = useUpdateProductPermission({
    productLevelAccessPermissionId,
  });

  return (
    <HStack className="gap-9 p-10 h-[calc(100vh-64px)]">
      <AddStepperNavigation
        baseLink={BASE_LINK}
        currentHash={hash}
        isEdit={true}
      />

      <VStack className="gap-6 w-full">
        {mutatePending && <Processing hash={hash} />}

        {!mutatePending && <AddStepperStepTitleCard hash={hash} />}

        {!mutatePending && hash === PRODUCT_PERMISSION_CREATE_HASH.FORM && (
          <BasicForm
            basicFormState={basicFormState}
            handleChange={handleChange}
            isDuplicateName={isDuplicateName}
          />
        )}
        {!mutatePending &&
          hash === PRODUCT_PERMISSION_CREATE_HASH.SET_PERMISSION && (
            <SetPermission
              categories={productLevelAccessCategoriesRes}
              checkCategory={checkCategory}
              checkScope={checkScope}
              handleCategoryChange={handleCategoryChange}
              handleScopeChange={handleScopeChange}
            />
          )}

        {!mutatePending &&
          hash === PRODUCT_PERMISSION_CREATE_HASH.REVIEW_THE_IMPACTED_ROLE && (
            <ReviewTheImpactedRoleDataTableCard
              roles={data?.roles ?? []}
              isPending={isPending}
            />
          )}

        {!mutatePending && (
          <AddStepperPanelProgress
            baseLink={BASE_LINK}
            currentHash={hash}
            basicFormState={basicFormState}
            setPermissionState={setPermissionState}
            mutate={mutate}
            isEdit={true}
            isDuplicateName={isDuplicateName}
          />
        )}

        {isComplete && (
          <AddStepperCompleteDialog
            completeMessage={t(
              'addStepperCompleteDialog.update.completeMessage',
            )}
            completeLinkButtonText={t(
              'addStepperCompleteDialog.update.completeLinkButtonText',
            )}
            completeLinkUrl="/settings/permission-and-roles/product-permissions"
          />
        )}
      </VStack>
    </HStack>
  );
}
